import { Injectable } from '@nestjs/common';
import { ErrorResponse } from '@gtpl/shared-models/common-models';
import { getManager, Repository } from 'typeorm';
import { InjectRepository } from '@nestjs/typeorm';
import { GenericTransactionManager } from 'apps/services/backend/typeorm-transactions/generic-transaction-manager';
import { ProductInspectionRepository } from '../product-inspection.repository';
import { ProductInspectionDTO } from '../dto/product-inspection.dto';
import { ProductInspection } from '../product-inspection.entity';
import { ProductInspecAdapter } from '../dto/product-inspection.adapter';
import { AllProductInspectionsResponse, ProductInspectionResponse } from '@gtpl/shared-models/production-management';
import { ProductInspecRequest } from '../dto/product-inspection.request';
import { CommonResponse } from '@gtpl/shared-models/masters';
import appSettings from 'apps/services/config';
const inspectionTable = appSettings.production_db + '.product_inspection'
const salesSoTable = appSettings.sales_db + '.sale_order';
const salesSoItemTable=appSettings.sales_db + '.sale_order_items'

@Injectable()
export class ProductInspectionService {

    constructor(
        @InjectRepository(ProductInspection)
        private repository: ProductInspectionRepository,
        private adapter: ProductInspecAdapter,
    ){}

    async getInspectionInfo(sampleNo: Number): Promise<ProductInspection>{
        const response = await this.repository.findOne({
            where: {sampleNo: sampleNo},
        });
        if(response){
            return response;
        }else{
            return null;
        }
    }

    async createProductInspectionInfo(info: ProductInspectionDTO, isUpdate: boolean): Promise<ProductInspectionResponse>{
        const manager = new GenericTransactionManager();
        try{
            await manager.startTransaction();
            if(!isUpdate){
               const entity = await this.getInspectionInfo(info.inspectionId);
               if(entity){
                throw new ErrorResponse(11104, 'Product Inspection already exists');
               }
            }else{
                const entity = await this.getInspectionInfo(info.inspectionId);
               if(entity){
                   if(entity.inspectionId != info.inspectionId){
                    throw new ErrorResponse(11104, 'Product Inspection already exists');
                   }                
               }
            }
            const convertedInfo = await this.adapter.convertDtoToEntity(info);
            const savedInfo = await manager.getCustomRepository(ProductInspectionRepository).save(convertedInfo);
            if(savedInfo){
                manager.completeTransaction();
                return new ProductInspectionResponse(true, 1111, 'Product Inspection Info Saved Successfully')
            }else{
                throw new ErrorResponse(9999, 'Failed to create Product Inspection Info');
            }
        }catch(error){
            manager.releaseTransaction();
            return error;
        }
    }

    async getAllProductInspections(): Promise<AllProductInspectionsResponse>{
        try{
            const manager = getManager()
            const query = `SELECT s.section as section, s.sale_order_id as saleOrderId,s.sale_order_item_id as saleOrderItemId,s.inspection_date as date,s.inspection_id as inspectionId,s.lot_number as lotNumber,so.sale_order_no as soNumber,  concat(b.brand_name, "/", p.name, "/", soi.min_grade_id,'-',soi.max_grade_id) as item FROM ` + inspectionTable + ` s 
            LEFT JOIN ` + salesSoItemTable + ` soi ON soi.sale_order_item_id=s.sale_order_item_id 
            LEFT JOIN ` + salesSoTable + ` so ON so.sale_order_id=s.sale_order_id
            LEFT JOIN ` + appSettings.masters_db + `.brands b ON b.brand_id=soi.brand_id
            LEFT JOIN `+ appSettings.masters_db + `.products p ON p.product_id=soi.item_id
            LEFT JOIN `+ appSettings.masters_db + `.sku_codes sc ON sc.sku_code_id=soi.varient_id
            ORDER BY  s.inspection_id ASC` 
            const qEntities: ProductInspectionDTO[] = await manager.query(query)
            if (qEntities) {

                const response = new AllProductInspectionsResponse(true, 1, 'Inspection Details retrieved successfully', qEntities);
                return response;
            } else {
                throw new ErrorResponse(99998, 'Data Not Found');
            }
        } catch (err) {
            console.log(err)
            return err;
        }
    }

    async getAllActiveProductInspections(): Promise<AllProductInspectionsResponse>{
        const manager = new GenericTransactionManager();
        try{
            await manager.startTransaction();
            const productInspecs: ProductInspectionDTO[] = [];
            const entities : ProductInspection[] = await manager.getCustomRepository(ProductInspectionRepository).find({
                order: {inspectionId: 'ASC'}, where:{isActive: true},
            });
            if(entities.length > 0){
                entities.forEach(Entity => {
                    const proInspec: ProductInspectionDTO = this.adapter.convertEntityToDto(Entity);
                    productInspecs.push(proInspec);
                });
                const response = new AllProductInspectionsResponse(true, 11208, 'All active product inspections are retrieved', productInspecs);
                manager.completeTransaction();
                return response;
            }else{
                throw new ErrorResponse(99998, 'No Records Found');
            }
        }catch(error){
            manager.releaseTransaction();
            throw error;        
        }
    }

    async  getProductInspectionById (inspectionId: number): Promise<ProductInspection>{
      const response = await this.repository.findOne({
          where: {inspectionId: inspectionId},
      });
      if(response){
          return response;
      }else{
          return null;
      }
    }

    async activateOrDeactivateInspection(req: ProductInspecRequest): Promise<ProductInspectionResponse>{
        try{
            const inspecExists = await this.getProductInspectionById(req.inspectionId);
            if(inspecExists){
                if(req.versionFlag != inspecExists.versionFlag){
                    throw new ErrorResponse(10113, 'Someone updated the current inspection information. Refresh and try again');
                }else{
                const inspecStatus = await this.repository.update(
                    {inspectionId: req.inspectionId},
                    {isActive: req.isActive, updatedUser: req.updatedUser}
                );
                if(inspecExists.isActive){
                    if(inspecStatus.affected){
                        const response: ProductInspectionResponse = new ProductInspectionResponse(true, 10115, 'Product Inspection is de-activated successfully');
                        return response;
                    }else{
                        throw new ErrorResponse(10111, 'Product Inspection is already de-activated');
                    }
                }else{
                    if(inspecStatus.affected){
                        const response: ProductInspectionResponse = new ProductInspectionResponse(true, 10115, 'Product Inspection is activated successfully');
                        return response;
                    }else{
                        throw new ErrorResponse(10111, 'Product Inspection is already activated');
                    }
                }
            }
            }else{
            throw new ErrorResponse(99998, 'No Records Found');
            }
        }catch(error){
            return error;
        }
    }

    async createSamples(inspectionInfo : ProductInspectionDTO):Promise<CommonResponse>{
        const manager = new GenericTransactionManager()
        try{
            console.log(inspectionInfo)
            const convertedEntity = this.adapter.convertDtoToEntity(inspectionInfo);
            
            await manager.startTransaction();
            const savedInfo = await manager.getCustomRepository(ProductInspectionRepository).save(convertedEntity);
            if(savedInfo){
                manager.completeTransaction();
                return new CommonResponse(true, 1111, 'Inspection along with Samples Info are created successfully');
            } else {
                throw new ErrorResponse(9999, 'Creation of Inspection failed');
            }
        } catch (error) {
            manager.releaseTransaction();
            return error;
        }
    }

    async getAllProductInspectionSamplesForSampleId(qReq: ProductInspecRequest): Promise<ProductInspectionResponse> {
        try {
            const qDtos: ProductInspectionDTO[] = [];
            const qEntities: ProductInspection = await this.repository.findOne({ where: { inspectionId: qReq.inspectionId }, relations: ['samplesInfo'] });
           console.log("test",qReq.inspectionId)
            if (qEntities) {
                const convertedDto: ProductInspectionDTO = this.adapter.convertEntityToLabelDto(
                    qEntities
                );
                const response = new ProductInspectionResponse(true, 1, 'Inspection Data retrieved successfully', convertedDto);
                return response;
            } else {
                
                throw new ErrorResponse(99998, 'Data Not Found');
            }
        } catch (err) {
            return err;
        }
    }
}
